import React, { useEffect, useState } from 'react'
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Button, FormControl, Select, MenuItem, InputLabel, Typography, Pagination, CircularProgress, Divider,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from "../../hooks/useDebounce";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import apiClient from '../../utils/apiClient';

const DisplayUsers = () => {
  const [users, setUsers] = useState([])
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const debouncedSearch = useDebounce(searchInput, 500);

  const navigate = useNavigate()

  const getAllUsers = async () => {
    try {
      setLoading(true)
      const params = { page, limit }
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }
      // if (search) params.search = search

      const res = await apiClient.get('/user/allusers', { params });
      if (res?.data?.status === true) {

        setUsers(res.data.users || [])
        setTotalPages(res.data.totalPages || 1)
        setTotalItems(res.data.totalItems || res.data.users.length)
      }
    } catch (error) {
      setError(error?.response?.data?.message || "Error fetching users")
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    getAllUsers();
  }, [page, limit, debouncedSearch]);

  const viewUserDetails = (id) => {
    navigate('/view-user-details', { state: id })
  }

  const handleDownload = async () => {
    try {

      const response = await apiClient.get('/user/download/users', {
        responseType: "blob",
      });

      const blob = new Blob(
        [response.data],
        {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.setAttribute("download", `users_${Date.now()}.xlsx`);

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);

    } catch (error) {

      console.error("Download failed:", error);

    }
  };

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }}>
      <Typography variant="h4" gutterBottom>
        User List
      </Typography>
      <Divider sx={{}} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        mb={2}
        mt={2}
      >
        {/* LEFT SIDE (Search) */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Search by name or phone"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </Box>

        {/* RIGHT SIDE (Download + Rows per page) */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>

          <Button
            variant="contained"
            sx={{ borderRadius: "8px", padding: "8px 20px", display: "flex", gap: 1 }}
            onClick={handleDownload}
          >
            <FileDownloadIcon fontSize="small" />
            <Typography>Download</Typography>
          </Button>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Rows per page</InputLabel>
            <Select
              value={limit}
              label="Rows per page"
              onChange={(e) => {
                setLimit(Number(e.target.value))
                setPage(1)
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
            </Select>
          </FormControl>

        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height={200}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Total Users: {totalItems}
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Referral Code</TableCell>
                  <TableCell>Is Mobile Verified</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user, index) => (
                    <TableRow key={user.id}>
                      <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                      <TableCell>{user.name || '-'}</TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                      <TableCell>{user.phone || '-'}</TableCell>
                      <TableCell>{user.referralCode || '-'}</TableCell>
                      <TableCell>{user.isMobileVerified ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <Button variant="outlined" color="primary" onClick={() => viewUserDetails(user.id)}>
                          View more
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box mt={2} display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  height: 30,
                  minWidth: 30,
                  fontSize: '1rem',
                  marginTop: '10px'
                }
              }}
            />
          </Box>
        </>
      )}
    </Box>
  )
}

export default DisplayUsers
